import { useState } from "react";
import { toast } from "sonner";
import {
  modelRegistryApi,
  registryErrorMessage,
} from "@/services/modelRegistryApi";
import type { ModelProvider } from "@/types/modelRegistry";
import { Btn, Field, Modal, TextInput } from "./ui";

interface ProviderModalProps {
  open: boolean;
  onClose: () => void;
  provider: ModelProvider | null;
  onSaved: () => Promise<void>;
}

export function ProviderModal({
  open,
  onClose,
  provider,
  onSaved,
}: ProviderModalProps) {
  const [form, setForm] = useState({
    slug: provider?.slug ?? "",
    name: provider?.name ?? "",
    baseUrl: provider?.baseUrl ?? "",
    apiKeyEnv: provider?.apiKeyEnv ?? "",
  });
  const [busy, setBusy] = useState(false);
  const set = (key: keyof typeof form, value: string) =>
    setForm((f) => ({ ...f, [key]: value }));

  const save = async () => {
    setBusy(true);
    try {
      await modelRegistryApi.upsertProvider({
        slug: form.slug,
        name: form.name,
        baseUrl: form.baseUrl.trim() || null,
        apiKeyEnv: form.apiKeyEnv.trim() || null,
      });
      toast.success(`${form.name} saved`);
      await onSaved();
      onClose();
    } catch (error) {
      toast.error(registryErrorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={provider ? `Edit ${provider.name}` : "Add a provider"}
      description="A provider is where requests go: a base URL and the env key that holds its secret."
      footer={
        <>
          <Btn variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Btn>
          <Btn
            onClick={() => void save()}
            disabled={!form.slug || !form.name || busy}
          >
            {provider ? "Save" : "Add provider"}
          </Btn>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-4">
        <Field label="Name">
          <TextInput
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
          />
        </Field>
        <Field label="Slug" hint="Cannot change later.">
          <TextInput
            value={form.slug}
            onChange={(e) => set("slug", e.target.value)}
            disabled={!!provider}
            className="font-mono"
          />
        </Field>
      </div>
      <Field label="Base URL" hint="Blank uses the SDK default.">
        <TextInput
          value={form.baseUrl}
          onChange={(e) => set("baseUrl", e.target.value)}
          placeholder="https://api.example.com/v1"
          className="font-mono"
        />
      </Field>
      <Field
        label="API key env"
        hint="The env key name, never the secret itself."
      >
        <TextInput
          value={form.apiKeyEnv}
          onChange={(e) => set("apiKeyEnv", e.target.value)}
          placeholder="OPENROUTER_API_KEY"
          className="font-mono"
        />
      </Field>
    </Modal>
  );
}
